import { createSelector } from 'reselect';
import {
  IntegrationServiceId,
  IntegrationConfigOrigin,
} from '@fullstory/client-api/goimports/fs_services_emissary_tsexport';
import { rootSelector } from '../selectors';
import {
  InstalledIntegration,
  IntegrationConfig,
  IntegrationListing,
  IntegrationModel,
} from '@fullstory/proto/fs/services/emissary';

const DESTINATION_INTEGRATION_SERVICE_IDS = [IntegrationServiceId.DestinationWebhooks];

const isDestinationServiceListing = (listing: IntegrationListing): boolean =>
  DESTINATION_INTEGRATION_SERVICE_IDS.some(id => id.toString() === listing.serviceId);

export const getAvailableDestinationsIntegrations = createSelector(rootSelector, settings =>
  (settings.integrations.availableIntegrations ?? []).filter(isDestinationServiceListing),
);

export const getAvailableStandardIntegrations = createSelector(rootSelector, settings =>
  (settings.integrations.availableIntegrations ?? []).filter(i => !isDestinationServiceListing(i)),
);

// This selector serves as a dependency of other selectors that need installed integration data. If we pass the root
// selector to every selector, any updates will break memoization for the others, which can easily lead to infinite
// loops that can crash the app.
//
// TODO(craigmccown): Consider giving a similar treatment to other selectors
const getInstalledIntegrations = createSelector(
  rootSelector,
  settings => settings.integrations.installedIntegrations,
);

export interface InstalledIntegrationWithDefaultConfig
  extends Omit<IntegrationModel, 'configs' | 'installed'>,
    InstalledIntegration {
  config: IntegrationConfig | null;
}

export const getInstalledIntegrationsWithDefaultConfigs = createSelector(
  getInstalledIntegrations,
  models =>
    models.map(
      ({ configs, installed, ...rest }): InstalledIntegrationWithDefaultConfig => ({
        ...rest,
        ...installed!,
        config:
          configs.find(
            c => c.origin === IntegrationConfigOrigin.INTEGRATION_CONFIG_ORIGIN_EMISSARY,
          ) ?? null,
      }),
    ),
);

export const getInstalledIntegrationsWithMetricAlertsConfigs = createSelector(
  getInstalledIntegrations,
  models =>
    models.map(i => ({
      ...i,
      configs: i.configs.filter(
        c => c.origin === IntegrationConfigOrigin.INTEGRATION_CONFIG_ORIGIN_METRIC_ALERTS,
      ),
    })),
);

export const getInstalledIntegrationsWithDataDestinationsConfigs = createSelector(
  getInstalledIntegrations,
  models =>
    models.map(i => ({
      ...i,
      configs: i.configs.filter(
        c => c.origin === IntegrationConfigOrigin.INTEGRATION_CONFIG_ORIGIN_DATA_DESTINATIONS,
      ),
    })),
);

export const getIntegrationDetails = createSelector(
  rootSelector,
  settings => settings.integrations.integrationDetails || {},
);

export const getAuthTokenStatuses = createSelector(
  rootSelector,
  settings => settings.integrations.authTokenStatuses || {},
);

export const getIntegrationDetailsLoading = createSelector(
  rootSelector,
  settings => settings.integrations.getIntegrationDetailsLoading || false,
);

export const getIntegrationUsers = createSelector(
  rootSelector,
  settings => settings.integrations.integrationUsers || [],
);
